/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import AskDialog from '../../../components/common/dialog/AskDialog';
import {
  listPartnerEmployees,
  unloadEmployees,
} from '../../../modules/partnerModule/employees';
import * as partnerApi from '../../../lib/api/partner';
import ParEmpList from '../../../components/partnermng/employees/ParEmpList';

const ParEmpListContainer = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [rowModesModel, setRowModesModel] = useState({});

  const { partner, employees, error, loading } = useSelector(
    ({ partnerModule, loading }) => ({
      partner: partnerModule.partner.partner,
      employees: partnerModule.partnerEmployees.employees,
      error: partnerModule.partnerEmployees.error,
      loading: loading['partner/LIST_PARTNER_EMPLOYEES'],
    }),
  );

  useEffect(() => {
    dispatch(listPartnerEmployees(partner.partnerUuid));
    return () => {
      dispatch(unloadEmployees());
    };
  }, []);

  const apiRef = useGridApiRef();

  const handleRowEditStart = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleCellFocusOut = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleEditClick = useCallback((id) => (event) => {
    event.stopPropagation();
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: 'edit' },
    }));
  }, []);

  const handleSaveClick = useCallback((id) => (event) => {
    event.stopPropagation();
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: 'view' },
    }));
  }, []);

  const handleCancelClick = useCallback((id) => (event) => {
    event.stopPropagation();
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: 'view', ignoreModifications: true },
    }));
  }, []);

  // row 수정이 완료(commit)되면 API 호출
  const handleProcessRowUpdate = async (newRow) => {
    try {
      const response = await partnerApi.editPartnerEmp(newRow);
      if (response.data.result === 'success') {
        enqueueSnackbar('수정 성공', { variant: 'success' });
        return newRow;
      }
    } catch (err) {
      const message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
    return newRow;
  };

  const [removeId, setRemoveId] = useState(null);
  const handleDeleteClick = useCallback((id) => (event) => {
    event.stopPropagation();
    setVisible(true);
    setRemoveId(id);
  }, []);

  const onRemove = async () => {
    try {
      const res = await partnerApi.removePartnerEmp(
        partner.partnerUuid,
        removeId,
      );
      if (res) {
        dispatch(listPartnerEmployees(partner.partnerUuid));
        enqueueSnackbar('삭제 성공', { variant: 'success' });
      }
    } catch (err) {
      const message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
    setRemoveId(null);
  };

  const columns = [
    { field: 'id', headerName: 'ID', type: 'string', editable: false },
    { field: 'employeeUuid', type: 'string', editable: false },
    { field: 'employeeNm', headerName: '담당자명', minWidth: 100, flex: 1, type: 'string', editable: true },
    { field: 'employeeGrade', headerName: '직급', minWidth: 100, flex: 1, type: 'string', editable: true },
    { field: 'dutiesNm', headerName: '담당업무', minWidth: 200, flex: 1, type: 'string', editable: true },
    { field: 'telNo', headerName: '연락처', minWidth: 200, flex: 1, type: 'string', editable: true },
    { field: 'emailAddr', headerName: '이메일', minWidth: 150, flex: 1, type: 'string', editable: true },
    { field: 'employeeDesc', headerName: '비고', minWidth: 200, flex: 1, type: 'string', editable: true },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === 'edit';

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
              color="primary"
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const [visible, setVisible] = useState(false);

  const onCancle = () => {
    setVisible(false);
    setRemoveId(null);
  };

  const onConfirm = () => {
    setVisible(false);
    onRemove();
  };

  return (
    <>
      <ParEmpList
        apiRef={apiRef}
        columns={columns}
        employees={employees}
        error={error}
        loading={loading}
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        handleRowEditStart={handleRowEditStart}
        handleRowEditStop={handleRowEditStop}
        handleCellFocusOut={handleCellFocusOut}
        processRowUpdate={handleProcessRowUpdate}
      />
      <AskDialog
        visible={visible}
        title="거래처 담당자 삭제"
        description="선택한 거래처 담당자를 삭제하시겠습니까?"
        confirmText="삭제"
        onConfirm={onConfirm}
        onCancle={onCancle}
      />
    </>
  );
};

export default ParEmpListContainer;
